/**
 * @fileoverview Detector de estado de red para mejorar mensajes de error
 * Diferencia entre errores de red (offline) y timeouts del servidor
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

import type { AxiosError } from 'axios';

/**
 * Verifica si el navegador está online según navigator.onLine
 * @returns true si el navegador reporta estar online
 *
 * @example
 * if (!isNavigatorOnline()) {
 *   console.log('Sin conexión a internet');
 * }
 */
export const isNavigatorOnline = (): boolean => {
  return typeof navigator !== 'undefined' && navigator.onLine;
};

/**
 * Verifica si un error de axios es debido a que el usuario está offline
 * Combina navigator.onLine con la ausencia de respuesta del servidor
 *
 * @param error - Error de axios a verificar
 * @returns true si el error es por estar offline
 *
 * @example
 * if (isOfflineError(error)) {
 *   showToast('Sin conexión a internet');
 * }
 */
export const isOfflineError = (error: AxiosError): boolean => {
  // Sin respuesta del servidor Y navegador reporta offline
  return !error.response && !isNavigatorOnline();
};

/**
 * Verifica si un error es un timeout del servidor
 * El servidor no respondió a tiempo pero hay conexión
 *
 * @param error - Error de axios a verificar
 * @returns true si el error es un timeout
 *
 * @example
 * if (isTimeoutError(error)) {
 *   showToast('El servidor tardó demasiado en responder');
 * }
 */
export const isTimeoutError = (error: AxiosError): boolean => {
  return error.code === 'ECONNABORTED' || error.message.includes('timeout');
};

/**
 * Verifica si un error es de red pero el usuario está online
 * Probablemente un problema del servidor o DNS
 *
 * @param error - Error de axios a verificar
 * @returns true si es error de red con conexión activa
 *
 * @example
 * if (isNetworkErrorWhileOnline(error)) {
 *   showToast('No se pudo conectar con el servidor');
 * }
 */
export const isNetworkErrorWhileOnline = (error: AxiosError): boolean => {
  // Sin respuesta PERO navegador reporta online
  return !error.response && isNavigatorOnline() && !isTimeoutError(error);
};

/**
 * Obtiene un mensaje descriptivo según el tipo de error de red
 * @param error - Error de axios
 * @returns Mensaje descriptivo del error
 *
 * @example
 * const message = getNetworkErrorMessage(error);
 * // 'Sin conexión a internet. Verifica tu conexión.'
 */
export const getNetworkErrorMessage = (error: AxiosError): string => {
  if (isTimeoutError(error)) {
    return 'El servidor tardó demasiado en responder. Por favor, intenta nuevamente.';
  }

  if (isNetworkErrorWhileOnline(error)) {
    return 'No se pudo conectar con el servidor. Verifica que el servicio esté disponible.';
  }

  // Fallback genérico
  return 'Error de red. Por favor, verifica tu conexión e intenta nuevamente.';
};

/**
 * Tipo de error de red detectado
 */
export type NetworkErrorType = 'offline' | 'timeout' | 'server-unreachable' | 'unknown';

/**
 * Detecta el tipo específico de error de red
 * @param error - Error de axios
 * @returns Tipo de error detectado
 *
 * @example
 * const errorType = detectNetworkErrorType(error);
 * switch (errorType) {
 *   case 'offline':
 *     // Mostrar mensaje de offline
 *     break;
 *   case 'timeout':
 *     // Mostrar mensaje de timeout
 *     break;
 * }
 */
export const detectNetworkErrorType = (error: AxiosError): NetworkErrorType => {
  if (isOfflineError(error)) {
    return 'offline';
  }

  if (isTimeoutError(error)) {
    return 'timeout';
  }

  if (isNetworkErrorWhileOnline(error)) {
    return 'server-unreachable';
  }

  return 'unknown';
};
